import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

// Magic-link return page. supabase-js consumes the URL hash asynchronously
// inside createClient (detectSessionInUrl: true). If we Navigate() before
// that finishes, react-router's history.replace clears the hash and the
// session is never established.
//
// So: wait for onAuthStateChange('SIGNED_IN') or a getSession() success,
// then go home. If neither happens within a small timeout, treat it as
// failure and send the user back to /login with an error flag.
export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let done = false;

    const sub = supabase.auth.onAuthStateChange((event, session) => {
      if (done) return;
      if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED')) {
        done = true;
        navigate('/', { replace: true });
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (done || !data.session) return;
      done = true;
      navigate('/', { replace: true });
    });

    const t = window.setTimeout(() => {
      if (done) return;
      done = true;
      setError('Link expired or already used. Try requesting a new one.');
    }, 8000);

    return () => {
      sub.data.subscription.unsubscribe();
      window.clearTimeout(t);
    };
  }, [navigate]);

  if (error) return <Navigate to={`/login?error=${encodeURIComponent(error)}`} replace />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg text-fg-muted">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-thermal-orange border-t-transparent" />
        <p className="text-sm">Tuning in…</p>
      </div>
    </div>
  );
}
